// Cross-host landing for a staff support-login. The admin app opens this URL in
// a new tab with a one-time ?token; we POST it to the customer host's redeem
// endpoint (which sets the crauto-auth cookie on THIS origin), then hydrate the
// session and drop into the app. Public route — the token is the bearer of
// authority; no existing session is required.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader } from '@progress/kendo-react-indicators';
import { apiFetch, ApiError } from '../../lib/apiClient';
import { useAuth } from '../../lib/auth';

export function Impersonate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get('token');
  const [error, setError] = useState<string | null>(null);
  // Redeem is single-use; guard against React 18 StrictMode double-invoke so we
  // never burn the token twice (the second call would 401). Track the specific
  // token so navigating the same tab to a fresh ?token= still redeems.
  const startedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    // The missing-token case is handled at render time (below), so the effect
    // only runs the single-use redeem when a token is actually present — no
    // synchronous setState in the effect body.
    if (!token || startedTokenRef.current === token) return;
    startedTokenRef.current = token;

    void (async () => {
      try {
        await apiFetch<void>('/api/auth/impersonate/redeem', {
          method: 'POST',
          body: { token },
        });
        await refresh();
        navigate('/', { replace: true });
      } catch (e) {
        const msg =
          e instanceof ApiError && e.status === 401
            ? 'This support link has expired or was already used. Start a new session from the admin console.'
            : 'Could not start the support session.';
        setError(msg);
      }
    })();
  }, [token, navigate, refresh]);

  const shownError = error ?? (!token ? 'This support link is missing its token.' : null);

  if (shownError) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', padding: 24 }}>
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <p style={{ marginBottom: 16 }}>{shownError}</p>
          <a href="/login">Go to sign in</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
      <Loader size="large" type="converging-spinner" />
    </div>
  );
}
